import * as React from 'react';
import { createPortal } from 'react-dom';
import { Box, TextAction, VisuallyHidden } from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';
import { Link } from 'react-router-dom';
import { ResponsiveHeaderNav } from '@leagueplatform/solutions-foundation';
import logoWithText from 'assets/GeisingerLogo.svg';
import { APP_MANAGED_ROUTES } from 'common/app-managed-routes';
import { AccountDeletionModal } from 'components/modals/account-deletion/account-deletion.component';
import { NavIndicator } from 'components/nav-indicator/nav-indicator.component';
import { useUserMenuConfig } from 'components/header-nav/use-user-menu-config.hook';
import {
  useUnreadMessageArrivalAnnouncement,
  useUnreadMessageCount,
} from 'hooks/use-unread-message-count/use-unread-message-count.hook';
import { useNavLinksList } from './use-nav-links-list.hook';
import { useSupportNavEntries } from './use-support-nav-entries.hook';
import { UnreadMessageCountRefresher } from './unread-message-count-refresher.component';

const Logo = () => {
  const { formatMessage } = useIntl();
  return (
    <TextAction as={Link} to={APP_MANAGED_ROUTES.HOME}>
      <img
        src={logoWithText}
        height="32px"
        alt={formatMessage({ id: 'STR_APP_NAME' })}
      />
    </TextAction>
  );
};

// Per-link overrides for the desktop nav links. `&&` doubles class specificity
// to win over the SDK's base inactive `color: $onSurfaceTextSubdued`. The user
// menu's item styling (including the critical Sign out) comes from the SDK's
// typed item props (`variant`/`css`) in `use-user-menu-config`.
const navLinkStyling = {
  '&&': { color: '$onSurfaceTextPrimary' },
  '&[aria-current="page"]:after': { borderRadius: '$small' },
};

export const AppNavBarComponent = React.memo(() => {
  const { formatMessage } = useIntl();
  const [isDeactivateOpen, setIsDeactivateOpen] = React.useState(false);
  const { navLinks } = useNavLinksList();
  const { userMenu } = useUserMenuConfig({
    onDeactivateClick: () => setIsDeactivateOpen(true),
  });
  // `data` is undefined until the query resolves and on failure, both of which
  // read as "nothing unread" — the indicator stays hidden rather than blocking
  // the nav.
  const { data: unreadMessageCount } = useUnreadMessageCount();
  const hasUnreadMessages = (unreadMessageCount ?? 0) > 0;

  const supportLabel = navLinks.find(
    (navLink) => navLink.to === APP_MANAGED_ROUTES.SUPPORT,
  )?.label;
  const { navRef, horizontalEntry, collapsedEntry } =
    useSupportNavEntries(supportLabel);

  const unreadIndicatorLabel = formatMessage({
    id: 'STR_UNREAD_MESSAGES_INDICATOR',
  });
  // A dot appearing is silent, and the indicator's own label only reaches someone
  // who visits the entry, so an arrival mid-session is announced separately.
  const unreadArrivalAnnouncement = useUnreadMessageArrivalAnnouncement(
    unreadMessageCount,
    unreadIndicatorLabel,
  );

  return (
    <>
      {/* Renders nothing; it holds the route subscription so this component does
          not have to, and so stays memoized across navigations. */}
      <UnreadMessageCountRefresher />
      {/* `display: contents` keeps this wrapper out of the layout: it exists to
          scope the entry lookup, and as a real box it would become the sticky
          header's containing block and pin it to its own height, stopping the
          header from sticking on scroll. */}
      <Box
        ref={navRef}
        css={{
          display: 'contents',
          // The page body, not the sticky header, owns spare viewport height.
          '& > header': { flex: '0 0 auto' },
        }}
      >
        <ResponsiveHeaderNav
          navLinks={navLinks}
          logo={<Logo />}
          userMenu={userMenu}
          navLinkStyling={navLinkStyling}
        />
        {/* Only one of the two entries exists at a time — the horizontal nav
            and the collapsed menu never render together. */}
        {hasUnreadMessages &&
          horizontalEntry &&
          createPortal(
            <NavIndicator placement="overlay" label={unreadIndicatorLabel} />,
            horizontalEntry,
          )}
        {hasUnreadMessages &&
          collapsedEntry &&
          createPortal(
            <NavIndicator
              placement="afterLabel"
              label={unreadIndicatorLabel}
            />,
            collapsedEntry,
          )}
      </Box>
      {/* Outside the nav entries on purpose: a live region wrapping the dot would
          announce on first load and on every window refocus. This one is empty
          until the count actually rises, so it only speaks on arrival. */}
      <VisuallyHidden>
        <Box as="span" role="status">
          {unreadArrivalAnnouncement}
        </Box>
      </VisuallyHidden>
      <AccountDeletionModal
        isOpen={isDeactivateOpen}
        onClose={() => setIsDeactivateOpen(false)}
      />
    </>
  );
});

AppNavBarComponent.displayName = 'AppNavBarComponent';
